import { CommonModule } from '@angular/common';
import { Component, EventEmitter, HostBinding, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatSliderModule } from '@angular/material/slider';
import { map, of, Subscription, switchMap } from 'rxjs';
import { ChartFilterHandlerService } from '../../shared-services/chart-filter-handler.service';
import { ConfigService } from '../../shared-services/config.service';
import { GlobalFilterService } from '../../shared-services/global-filter-service';
import { ResponsiveLayoutService } from '../../shared-services/responsive-layout.service';

interface PricingLeverSliderConfig {
  widgetId?: string;
  title?: string;
  highlightText?: string;
  min?: number;
  max?: number;
  step?: number;
  value?: number;
  defaultValue?: number;
  inputLabel?: string;
  suffix?: string;
  filterKey?: string | string[];
  filterListener?: string | string[];
  filterListner?: string | string[];
  key?: string;
  isQueryParam?: boolean;
  apiUrl?: string;
  method?: string;
  data?: unknown;
  dataPath?: string;
  valueKey?: string;
  minKey?: string;
  maxKey?: string;
  stepKey?: string;
  inheritApaPricingLever?: boolean;
  apaRangesUrl?: string;
  apaProductTypeKey?: string;
  apaRangeLabelKey?: string;
  apaRangeValueKey?: string;
  height?: string;
  heightSm?: string;
  heightMd?: string;
  heightLg?: string;
}

@Component({
  selector: 'app-pricing-lever-slider',
  standalone: true,
  imports: [CommonModule, FormsModule, MatSliderModule],
  templateUrl: './pricing-lever-slider.component.html',
  styleUrls: ['./pricing-lever-slider.component.scss'],
})
export class PricingLeverSliderComponent implements OnInit, OnChanges, OnDestroy {
  private static readonly APA_OVERRIDES_STORAGE_KEY = 'dvm_apa_range_overrides';
  @Input() data?: PricingLeverSliderConfig;
  @Output() valueChange = new EventEmitter<number>();

  min: number | null = null;
  max: number | null = null;
  step: number | null = null;
  value: number | null = null;
  inputValue: number | null = null;
  private committedValue: number | null = null;
  fontScale = 1;
  errorMessage = '';
  configReady = false;

  private readonly configService = inject(ConfigService);
  private readonly chartFilterHandler = inject(ChartFilterHandlerService);
  private readonly globalFilterService = inject(GlobalFilterService);
  private readonly responsiveLayoutService = inject(ResponsiveLayoutService);
  private dataSub?: Subscription;
  private reflowSub?: Subscription;
  private filterSub?: Subscription;
  private lastEmittedValue: number | null = null;
  private apaInitialized = false;
  private userHasChangedValue = false;
  private lastResolvedProductType = '';

  ngOnInit(): void {
    this.applyResponsiveSizing();
    this.reflowSub = this.responsiveLayoutService.reflow$.subscribe(() => this.applyResponsiveSizing());
    this.filterSub = this.globalFilterService.filter$.subscribe(() => this.handleFilterDrivenInitialization());
    this.loadConfig();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['data'] && !changes['data'].firstChange) {
      this.applyResponsiveSizing();
      this.loadConfig();
    }
  }

  ngOnDestroy(): void {
    this.dataSub?.unsubscribe();
    this.reflowSub?.unsubscribe();
    this.filterSub?.unsubscribe();
  }

  @HostBinding('style.--pricing-slider-font-scale')
  get hostFontScale(): string {
    return this.fontScale.toString();
  }

  get suffix(): string {
    return this.data?.suffix ?? '%';
  }

  get formattedMin(): string {
    return this.formatPercent(this.min ?? 0);
  }

  get formattedMax(): string {
    return this.formatPercent(this.max ?? 0);
  }

  get formattedValue(): string {
    return this.value === null ? '' : this.formatPercent(this.value);
  }

  onSliderInput(nextValue: number): void {
    this.commitValue(nextValue);
  }

  onInputChange(): void {
    this.commitValue(this.inputValue);
  }

  private loadConfig(): void {
    this.dataSub?.unsubscribe();
    this.errorMessage = '';
    const currentFilters = this.globalFilterService.getCurrentFilters() || {};

    if (!this.data?.apiUrl) {
      this.applyConfig(this.applyApaPricingLeverToSource(this.data));
      return;
    }

    this.dataSub = this.configService.getConfig(this.data.apiUrl, this.data, currentFilters, this.data.method).pipe(
      switchMap((response) => {
        if (!this.data?.inheritApaPricingLever) {
          return of(response);
        }

        const apaUrl = String(
          this.data?.apaRangesUrl || 'assets/mock-data/dvm/account-classification/apa-ranges.json',
        ).trim();

        if (!apaUrl) {
          return of(response);
        }

        return this.configService.getData(apaUrl).pipe(
          map((apaResponse) => this.applyApaPricingLeverToSource(response, apaResponse)),
        );
      }),
    ).subscribe({
      next: (response) => this.applyConfig(response),
      error: () => {
        this.errorMessage = 'Unable to load pricing lever data.';
        this.applyConfig(this.applyApaPricingLeverToSource(this.data));
      },
    });
  }

  private applyConfig(source: unknown): void {
    const record = this.resolveConfigRecord(source);

    const min = this.readRequiredNumber(record, this.data?.minKey || 'min', this.data?.min);
    const max = this.readRequiredNumber(record, this.data?.maxKey || 'max', this.data?.max);
    const step = this.readRequiredNumber(record, this.data?.stepKey || 'step', this.data?.step);

    const valueKey = this.data?.valueKey || 'value';
    const value = this.readRequiredNumber(record, valueKey, this.data?.value ?? this.data?.defaultValue);

    if (min === null || max === null || step === null || value === null) {
      this.configReady = false;
      this.errorMessage = 'Pricing lever config must provide min, max, step, and value.';
      return;
    }

    this.min = min;
    this.max = max;
    this.step = step;
    this.configReady = true;
    this.setDisplayValue(value);
    this.committedValue = this.value;
    this.lastEmittedValue = this.value;
    this.applyInitialFilter();
  }

  private applyInitialFilter(): void {
    const filterKey = this.data?.filterKey || this.data?.key;
    if (!filterKey || this.value === null) {
      return;
    }

    const filterKeys = Array.isArray(filterKey)
      ? filterKey
      : String(filterKey)
          .split(',')
          .map((key) => key.trim())
          .filter(Boolean);

    if (filterKeys.length === 0) {
      return;
    }

    const currentFilters = this.globalFilterService.getCurrentFilters();
    const hasExisting = filterKeys.some(
      (key) =>
        currentFilters?.requestParams?.[key] !== undefined ||
        currentFilters?.queryParams?.[key] !== undefined,
    );

    if (hasExisting && !this.data?.inheritApaPricingLever) {
      return;
    }

    const isQueryParam = Boolean(this.data?.isQueryParam);
    this.globalFilterService.updateFilterSelection(
      filterKeys,
      [this.value],
      isQueryParam,
      this.data?.widgetId,
      false,
      this.data?.filterListener || this.data?.filterListner,
    );
  }

  private resolveConfigRecord(source: unknown): Record<string, unknown> | null {
    const configuredPath = this.data?.dataPath;
    const candidates = [
      configuredPath ? this.resolvePath(source, configuredPath) : undefined,
      this.resolvePath(source, 'responseList.0.data.0.pricingLever'),
      this.resolvePath(source, 'responseList.0.data.0'),
      this.resolvePath(source, 'data.0.pricingLever'),
      this.resolvePath(source, 'data.0'),
      this.data?.data,
      source,
    ];

    for (const candidate of candidates) {
      if (candidate && typeof candidate === 'object' && !Array.isArray(candidate)) {
        return candidate as Record<string, unknown>;
      }
    }

    return null;
  }

  private commitValue(rawValue: unknown, emit = true): void {
    if (this.min === null || this.max === null || this.step === null) {
      return;
    }

    this.setDisplayValue(rawValue);

    if (!emit) {
      return;
    }

    this.userHasChangedValue = true;

    if (this.value === null || this.committedValue === this.value || this.lastEmittedValue === this.value) {
      return;
    }

    this.committedValue = this.value;
    this.lastEmittedValue = this.value;
    this.valueChange.emit(this.value);
    const filterKey = this.data?.filterKey || this.data?.key;

    if (filterKey) {
      const filterPayload = this.buildFilterPayload(filterKey, this.value);
      this.chartFilterHandler.updateFilterSelection(
        { ...this.data, filterKey },
        filterPayload,
        filterPayload,
      );
    }
  }

  private setDisplayValue(rawValue: unknown): void {
    if (this.min === null || this.max === null || this.step === null) {
      return;
    }

    const nextValue = this.clamp(this.toNumber(rawValue, this.value ?? this.min));
    const decimals = this.decimalPlaces(this.step);
    this.value = Number(nextValue.toFixed(decimals));
    this.inputValue = this.value;
  }

  private buildFilterPayload(filterKey: string | string[], value: number): Record<string, number> {
    const keys = Array.isArray(filterKey)
      ? filterKey
      : String(filterKey)
        .split(',')
        .map((key) => key.trim())
        .filter(Boolean);

    return keys.reduce<Record<string, number>>((payload, key) => {
      payload[key] = value;
      return payload;
    }, { value });
  }

  private applyResponsiveSizing(): void {
    this.fontScale = this.responsiveLayoutService.getFontScale();
  }

  private formatPercent(value: number): string {
    return `${value.toFixed(2)}${this.suffix}`;
  }

  private toNumber(value: unknown, fallback: number): number {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : fallback;
  }

  private readRequiredNumber(
    record: Record<string, unknown> | null,
    key: string,
    fallback?: number,
  ): number | null {
    const raw = record?.[key] ?? fallback;
    const parsed = Number(raw);
    return Number.isFinite(parsed) ? parsed : null;
  }

  private clamp(value: number): number {
    if (this.min === null || this.max === null) {
      return value;
    }

    return Math.min(this.max, Math.max(this.min, value));
  }

  private decimalPlaces(value: number): number {
    const text = String(value);
    return text.includes('.') ? text.split('.')[1].length : 0;
  }

  private resolvePath(source: unknown, path: string): unknown {
    if (!path) {
      return undefined;
    }

    return path.split('.').reduce<unknown>((current, segment) => {
      if (current === null || current === undefined) {
        return undefined;
      }
      if (Array.isArray(current)) {
        const index = Number(segment);
        return Number.isInteger(index) ? current[index] : undefined;
      }
      if (typeof current === 'object') {
        return (current as Record<string, unknown>)[segment];
      }
      return undefined;
    }, source);
  }

  private applyApaPricingLeverToSource(source: unknown, apaSource?: unknown): unknown {
    if (!this.data?.inheritApaPricingLever) {
      return source;
    }

    const baseRecord = this.resolveConfigRecord(source);
    const productType = this.resolveSelectedProductPriceType(source);
    if (!productType) {
      return baseRecord || source;
    }

    const apaRecord = this.resolveApaRangeRecord(productType, apaSource);
    if (!apaRecord) {
      return baseRecord || source;
    }

    this.lastResolvedProductType = productType;
    this.apaInitialized = true;

    const min = Number(apaRecord['min']);
    const max = Number(apaRecord['max']);
    const value = this.resolveApaValue(apaRecord);
    const step = this.resolveApaStep(min, max);

    const mergedRecord = {
      ...(baseRecord && typeof baseRecord === 'object' && !Array.isArray(baseRecord)
        ? baseRecord as Record<string, unknown>
        : {}),
      min,
      max,
      step,
      value,
      product_price_type: productType,
    };

    return mergedRecord;
  }

  private resolveSelectedProductPriceType(source: unknown): string {
    const key = this.data?.apaProductTypeKey || 'product_price_type';
    const currentFilters = this.globalFilterService.getCurrentFilters();
    const fromFilters =
      currentFilters?.requestParams?.[key] ??
      currentFilters?.queryParams?.[key];
    const normalizedFromFilters = Array.isArray(fromFilters) ? fromFilters[0] : fromFilters;
    if (normalizedFromFilters !== undefined && normalizedFromFilters !== null && normalizedFromFilters !== '') {
      return String(normalizedFromFilters);
    }

    const record = this.resolveConfigRecord(source);
    const fromRecord = record?.[key];
    if (fromRecord !== undefined && fromRecord !== null && fromRecord !== '') {
      return String(fromRecord);
    }

    return '';
  }

  private resolveApaRangeRecord(productType: string, apaSource?: unknown): Record<string, unknown> | null {
    const rows = Array.isArray((apaSource as any)?.responseList?.[0]?.data)
      ? (apaSource as any).responseList[0].data
      : Array.isArray((apaSource as any)?.data)
        ? (apaSource as any).data
        : Array.isArray(apaSource)
          ? apaSource
          : [];

    const labelKey = this.data?.apaRangeLabelKey || 'label';
    const normalizedProductType = this.normalizeApaLabel(productType);
    const matched = rows.find((row: any) => this.normalizeApaLabel(row?.[labelKey]) === normalizedProductType);
    return matched && typeof matched === 'object' ? matched as Record<string, unknown> : null;
  }

  private resolveApaValue(apaRecord: Record<string, unknown>): number {
    const labelKey = this.data?.apaRangeLabelKey || 'label';
    const valueKey = this.data?.apaRangeValueKey || 'value';
    const label = String(apaRecord?.[labelKey] || '');
    const overrides = this.readApaOverridesMap();
    const override = overrides[this.normalizeApaLabel(label)];
    const fallback = Number(apaRecord?.['currentValue'] ?? apaRecord?.[valueKey] ?? this.data?.value ?? this.data?.defaultValue ?? 0);
    const nextValue = override ?? fallback;
    return Number.isFinite(Number(nextValue)) ? Number(nextValue) : 0;
  }

  private resolveApaStep(min: number, max: number): number {
    const range = max - min;
    if (!Number.isFinite(range) || range <= 0) {
      return Number(this.data?.step) || 0.01;
    }

    const suggested = Number((range / 100).toFixed(2));
    return suggested > 0 ? suggested : 0.01;
  }

  private readApaOverridesMap(): Record<string, number> {
    try {
      const raw = sessionStorage.getItem(PricingLeverSliderComponent.APA_OVERRIDES_STORAGE_KEY);
      if (!raw) {
        return {};
      }
      const parsed = JSON.parse(raw);
      return parsed && typeof parsed === 'object' ? parsed : {};
    } catch {
      return {};
    }
  }

  private normalizeApaLabel(value: any): string {
    return String(value || '')
      .trim()
      .toLowerCase()
      .replace(/\s+/g, ' ');
  }

  private handleFilterDrivenInitialization(): void {
    if (!this.data?.inheritApaPricingLever || this.userHasChangedValue) {
      return;
    }

    const productType = this.resolveSelectedProductPriceType(this.data);
    if (!productType) {
      return;
    }

    if (this.apaInitialized && this.lastResolvedProductType === productType) {
      return;
    }

    this.loadConfig();
  }
}
